import { ENV_DOMAIN } from '@/shared/config';

/**
 * Splits document.cookie into name/value pairs without decoding either side. An entry with no `=` yields an empty value.
 *
 * @returns The `[name, value]` pairs currently visible to the page.
 */
const readEntries = () =>
  document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry): [string, string] => {
      const separatorIndex = entry.indexOf('=');
      return separatorIndex === -1
        ? [entry, '']
        : [entry.slice(0, separatorIndex), entry.slice(separatorIndex + 1)];
    });

/**
 * Reads the raw value of the cookie with exactly this name.
 *
 * @param name - Full cookie name, such as a registered `COOKIE_NAMES` entry or a Cognito key.
 * @returns The undecoded value, or undefined when no such cookie is visible.
 */
const get = (name: string) => readEntries().find(([entryName]) => entryName === name)?.[1];

/**
 * Reads the raw value of the first cookie whose name contains a fragment. Exists because the cookies that the portal's sign-in flow reads are set outside this repo and their exact names are not pinned down here.
 *
 * @param fragment - Text the cookie name must contain.
 * @returns The undecoded value, or undefined when no cookie name contains the fragment.
 */
const getByNameFragment = (fragment: string) =>
  readEntries().find(([entryName]) => entryName.includes(fragment))?.[1];

/**
 * Resolves the domain that portal-wide cookies are scoped to. A cookie only expires if it is cleared with the same domain it was set with, so Amplify's cookie storage and clearAll must share this value.
 *
 * @returns 'localhost' when running locally, otherwise the environment's cookie domain.
 */
const getDomain = () => (window.location.hostname === 'localhost' ? 'localhost' : ENV_DOMAIN);

/**
 * Expires every cookie visible to the page. Rewrites each one with an already-past expiry on path '/' and the shared cookie domain; HttpOnly cookies and cookies set on another path or domain are untouched.
 *
 * @returns Nothing.
 */
const clearAll = () => {
  const domain = getDomain();
  readEntries().forEach(([name]) => {
    document.cookie = `${name}=; expires=${new Date().toUTCString()}; path=/; domain=${domain}`;
  });
};

const cookies = { get, getByNameFragment, getDomain, clearAll };

export default cookies;
